"use client";

import React, { useState, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Globe, ArrowRight, Sparkles, Building2, Smartphone, Check, Laptop } from "lucide-react";

export default function PwaDestinationModal() {
  const router = useRouter();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [rememberChoice, setRememberChoice] = useState(false);

  useEffect(() => {
    // 1. Detect if running in standalone PWA mode or app parameter
    const isStandalone =
      typeof window !== "undefined" &&
      (window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true ||
        (document.referrer && document.referrer.includes("android-app://")) ||
        new URLSearchParams(window.location.search).get("pwa") === "1" ||
        new URLSearchParams(window.location.search).get("app") === "1");

    // Listen for manual trigger from header buttons: "switch-pwa-destination"
    const handleManualOpen = () => {
      setIsOpen(true);
    };
    window.addEventListener("open-pwa-destination-selector", handleManualOpen);

    if (!isStandalone) {
      return () => {
        window.removeEventListener("open-pwa-destination-selector", handleManualOpen);
      };
    }

    // Ensure clean state: Never hijack visitor navigation from website to CRM login
    try {
      if (localStorage.getItem("johnson_pwa_default_mode") === "crm" && !pathname.startsWith("/crm")) {
        localStorage.removeItem("johnson_pwa_default_mode");
      }
    } catch {}

    return () => {
      window.removeEventListener("open-pwa-destination-selector", handleManualOpen);
    };
  }, [pathname, router]);

  const handleSelectDestination = (destination: "crm" | "website") => {
    sessionStorage.setItem("johnson_pwa_session_routed", "true");

    if (rememberChoice) {
      localStorage.setItem("johnson_pwa_default_mode", destination);
    } else {
      localStorage.removeItem("johnson_pwa_default_mode");
    }

    setIsOpen(false);

    if (destination === "crm") {
      router.push("/crm");
    } else {
      router.push("/");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-950/85 backdrop-blur-xl animate-in fade-in duration-300">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 15 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800/90 rounded-3xl shadow-2xl shadow-cyan-500/10 overflow-hidden text-slate-900 dark:text-white"
          >
            {/* Header / Brand Banner */}
            <div className="px-6 pt-8 pb-4 text-center relative">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shadow-xl border border-cyan-500/30 overflow-hidden mb-3 bg-[#0B132B]">
                <img src="/brand/app-icon.svg?v=3" alt="Johnson Softwares" className="w-full h-full object-contain" />
              </div>
              <h2 className="font-heading font-black text-xl sm:text-2xl tracking-[0.14em] uppercase text-slate-900 dark:text-white">
                JOHNSON <span className="text-cyan-500 dark:text-cyan-400 tracking-[0.18em]">SOFTWARES</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
                Select which application workspace you would like to open:
              </p>
            </div>

            {/* Destination Selection Cards */}
            <div className="p-6 sm:p-8 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Option 1: CRM Enterprise Portal */}
              <button
                type="button"
                onClick={() => handleSelectDestination("crm")}
                className="group relative p-5 sm:p-6 rounded-2xl border-2 border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-950/60 hover:border-cyan-500 dark:hover:border-cyan-400 hover:bg-cyan-500/5 dark:hover:bg-cyan-500/10 transition-all text-left flex flex-col justify-between cursor-pointer active:scale-[0.98] shadow-sm hover:shadow-xl hover:shadow-cyan-500/10"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/10 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Shield className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30">
                      Private App
                    </span>
                  </div>
                  <h3 className="font-heading font-black text-base sm:text-lg text-slate-900 dark:text-white group-hover:text-cyan-500 transition-colors">
                    Sales CRM Portal
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
                    Visual pipeline kanban, Meta WhatsApp speed-to-lead bot, automated telecalling &amp; deal tracking.
                  </p>
                </div>

                <div className="mt-6 pt-3 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between text-xs font-bold text-cyan-600 dark:text-cyan-400">
                  <span>Launch CRM App</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>

              {/* Option 2: Company Website */}
              <button
                type="button"
                onClick={() => handleSelectDestination("website")}
                className="group relative p-5 sm:p-6 rounded-2xl border-2 border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-950/60 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-500/5 dark:hover:bg-blue-500/10 transition-all text-left flex flex-col justify-between cursor-pointer active:scale-[0.98] shadow-sm hover:shadow-xl hover:shadow-blue-500/10"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Globe className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30">
                      Public Site
                    </span>
                  </div>
                  <h3 className="font-heading font-black text-base sm:text-lg text-slate-900 dark:text-white group-hover:text-blue-500 transition-colors">
                    Company Website
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed">
                    Custom software engineering, AI agent systems, interactive demos, case studies &amp; pricing retainers.
                  </p>
                </div>

                <div className="mt-6 pt-3 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between text-xs font-bold text-blue-600 dark:text-blue-400">
                  <span>Visit Website</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            </div>

            {/* Footer with Remember Option */}
            <div className="px-6 sm:px-8 py-4 bg-slate-100/70 dark:bg-slate-950/80 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <label className="flex items-center gap-2 cursor-pointer select-none text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
                <input
                  type="checkbox"
                  checked={rememberChoice}
                  onChange={(e) => setRememberChoice(e.target.checked)}
                  className="rounded border-slate-300 dark:border-slate-700 text-primary focus:ring-primary h-4 w-4 cursor-pointer"
                />
                <span>Always remember my choice on this device</span>
              </label>

              <span className="text-[11px] text-slate-400 text-center sm:text-right">
                Switch anytime via the top navigation button
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
